import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';

const BrandingContext = createContext(null);

/** Used when platform branding has no logo_url */
export const DEFAULT_LOGO_URL = '/trackly-icon-original.png';

const DEFAULT_BRANDING = {
  platform_name: 'Trackly',
  logo_url: DEFAULT_LOGO_URL,
  favicon_url: null,
  mobile_logo_url: null,
  website: null,
  contact_email: null
};

// Helper to resolve image URLs - handles both relative and absolute URLs
const resolveImageUrl = (url) => {
  if (!url) return null;
  // If it's already an absolute URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Public frontend assets (e.g. /trackly-icon-original.png) — keep as-is
  if (url.startsWith('/') && !url.startsWith('/api/')) {
    return url;
  }
  // If it's a relative URL starting with /api, prefix with API base
  if (url.startsWith('/api/')) {
    // Get the base URL without the /api suffix
    const baseUrl = API.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  }
  // For other relative URLs, use the API base
  return `${API}${url.startsWith('/') ? url : '/' + url}`;
};

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(() => {
    // Try to get from localStorage first for faster initial load
    const cached = localStorage.getItem('platformBranding');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return {
          ...parsed,
          logo_url: parsed.logo_url || DEFAULT_LOGO_URL
        };
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(!branding);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/platform/branding`);
      const data = response.data;
      
      // Resolve image URLs before storing; fall back to default logo if missing
      const resolvedData = {
        ...data,
        logo_url: resolveImageUrl(data.logo_url) || DEFAULT_LOGO_URL,
        favicon_url: resolveImageUrl(data.favicon_url),
        mobile_logo_url: resolveImageUrl(data.mobile_logo_url)
      };
      
      setBranding(resolvedData);
      localStorage.setItem('platformBranding', JSON.stringify(resolvedData));
      
      // Update document title
      if (resolvedData.platform_name) {
        document.title = resolvedData.platform_name;
      }
      
      // Update favicon
      if (resolvedData.favicon_url) {
        const favicon = document.getElementById('dynamic-favicon');
        if (favicon) {
          favicon.href = resolvedData.favicon_url;
        }
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshBranding = () => {
    fetchBranding();
  };

  const value = {
    branding: branding
      ? { ...branding, logo_url: branding.logo_url || DEFAULT_LOGO_URL }
      : DEFAULT_BRANDING,
    loading,
    refreshBranding
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      branding: DEFAULT_BRANDING,
      loading: false,
      refreshBranding: () => {}
    };
  }
  return context;
}

export default BrandingContext;
